import { useState } from "react";
import { useParams } from "react-router-dom";
import { useOrderByPublicLink, useUpdateCustomerInfo } from "@/pages/order/hooks/useOrders";
import { displayClass, h1Class, metaLabelClass } from "@groot/ui/typography";
import { cn } from "@groot/ui/lib/utils";
import { LoadingState } from "@groot/ui/loading-spinner";
import { OrderStatusBadge } from "@/components/status-badges";
import { useLightTheme } from "@/lib/useLightTheme";
import { Button } from "@groot/ui/button";
import { Input } from "@groot/ui/input";
import { Label } from "@groot/ui/label";
import { Textarea } from "@groot/ui/textarea";
import { Smartphone, CheckCircle2, MapPin, Loader2, ExternalLink, Truck } from "lucide-react";
import { TrackingTimeline } from "./components/TrackingCard";

const UPI_ID = "reshmansusmi@yescred";
const UPI_NAME = "Riqah Design";

const STATUS_LABEL: Record<string, string> = {
  new: "Order Received",
  printing: "In Production",
  shipped: "Shipped",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

function ShippingInfoBanner({
  publicLink,
  existingEmail,
  existingInstagramHandle,
  existingMobileNumber,
}: {
  publicLink: string;
  existingEmail?: string | null;
  existingInstagramHandle?: string | null;
  existingMobileNumber?: string | null;
}) {
  const [address, setAddress] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [email, setEmail] = useState("");
  const [instagramHandle, setInstagramHandle] = useState("");
  const [error, setError] = useState("");

  const hasEmail = !!existingEmail;
  const hasInstagram = !!existingInstagramHandle;
  const hasMobileNumber = !!existingMobileNumber;
  const needsAddress = true; // always need address
  const needsMobileNumber = !hasMobileNumber;

  const mutation = useUpdateCustomerInfo(publicLink);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (!address.trim()) {
      setError("Please enter your shipping address");
      return;
    }

    if (needsMobileNumber && !mobileNumber.trim()) {
      setError("Please enter your mobile number");
      return;
    }

    mutation.mutate(
      {
        address: address.trim(),
        mobileNumber: hasMobileNumber ? existingMobileNumber! : mobileNumber.trim(),
        email: hasEmail ? existingEmail! : email.trim() || undefined,
        instagramHandle: hasInstagram
          ? existingInstagramHandle!
          : instagramHandle.trim() || undefined,
      },
      {
        onError: () => {
          setError("Something went wrong. Please try again.");
        },
      },
    );
  }

  const showOptionalFields = !hasEmail || !hasInstagram;

  return (
    <div className="px-6 py-5 border-t border-border bg-warning/5 border-l-2 border-l-warning">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex items-start gap-2.5">
          <MapPin className="size-4 text-warning mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-foreground">We need a few details</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              To ship your order, we need your delivery address and contact number.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          {needsAddress && (
            <div>
              <Label htmlFor="address" className="text-xs text-muted-foreground">
                Full address <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="address"
                placeholder="Door number, street, landmark, city, pin code"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className="mt-1 min-h-[72px] text-sm bg-background resize-none"
              />
            </div>
          )}

          {needsMobileNumber && (
            <div>
              <Label htmlFor="mobileNumber" className="text-xs text-muted-foreground">
                Mobile number <span className="text-destructive">*</span>
              </Label>
              <Input
                id="mobileNumber"
                type="tel"
                placeholder="10-digit mobile number"
                value={mobileNumber}
                onChange={(e) => setMobileNumber(e.target.value)}
                className="mt-1 h-9 text-sm bg-background"
              />
            </div>
          )}

          {showOptionalFields && (
            <div className="grid grid-cols-2 gap-3">
              {!hasEmail && (
                <div>
                  <Label htmlFor="email" className="text-xs text-muted-foreground">
                    Email address <span className="text-muted-foreground">(optional)</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 h-9 text-sm bg-background"
                  />
                </div>
              )}
              {!hasInstagram && (
                <div>
                  <Label htmlFor="instagramHandle" className="text-xs text-muted-foreground">
                    Instagram <span className="text-muted-foreground">(optional)</span>
                  </Label>
                  <Input
                    id="instagramHandle"
                    placeholder="@yourhandle"
                    value={instagramHandle}
                    onChange={(e) => setInstagramHandle(e.target.value)}
                    className="mt-1 h-9 text-sm bg-background"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {error && <p className="text-xs text-destructive">{error}</p>}

        <Button type="submit" size="sm" className="w-full" disabled={mutation.isPending}>
          {mutation.isPending ? (
            <>
              <Loader2 className="size-3.5 animate-spin" />
              Saving…
            </>
          ) : (
            "Save & Continue"
          )}
        </Button>
      </form>
    </div>
  );
}

export function PublicOrderDetail() {
  useLightTheme();
  const { publicLink } = useParams<{ publicLink: string }>();
  const { data: order, isLoading } = useOrderByPublicLink(publicLink || "");

  if (isLoading) {
    return (
      <div className="min-h-screen bg-muted/50 flex items-center justify-center">
        <LoadingState text="Loading…" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-muted/50 flex items-center justify-center p-4">
        <div className="bg-card rounded-2xl shadow-sm p-8 max-w-sm w-full text-center">
          <p className="font-semibold text-foreground">Link not found</p>
          <p className="text-sm text-muted-foreground mt-1">
            This order link is invalid or has expired.
          </p>
        </div>
      </div>
    );
  }

  const subtotal = order.items?.reduce((sum, item) => sum + Number(item.lineTotal), 0) ?? 0;
  const discountType = order.discountType;
  const discountValue = Number(order.discountValue ?? 0);
  const discountAmount = Number(order.discountAmount ?? 0);
  const total = Number(order.total ?? 0);

  const upiUrl = `upi://pay?pa=${UPI_ID}&pn=${encodeURIComponent(UPI_NAME)}&am=${total.toFixed(2)}&tn=${encodeURIComponent(`Order ${order.orderNumber}`)}&cu=INR`;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&color=000000&bgcolor=ffffff&data=${encodeURIComponent(upiUrl)}`;

  const isPaid = order.paymentStatus === "paid";

  return (
    <div className="min-h-screen bg-muted/50 py-8 px-4">
      <div className="max-w-[480px] mx-auto bg-card rounded-2xl shadow-sm overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 flex items-center gap-2.5">
          <img src="/logo.png" alt="Riqah Design" className="h-12 w-auto" />
          <span className="text-sm font-semibold text-foreground">Riqah Design</span>
        </div>

        {/* Shipping info banner — shown when customer has no address or mobile number */}
        {(!order.customer?.address || !order.customer?.mobileNumber) && (
          <ShippingInfoBanner
            publicLink={publicLink!}
            existingEmail={order.customer?.email}
            existingInstagramHandle={order.customer?.instagramHandle}
            existingMobileNumber={order.customer?.mobileNumber}
          />
        )}

        {/* Hero */}
        <div className="px-6 py-6 border-t border-border">
          <p className="text-sm text-muted-foreground">
            Hello{order.customer?.name ? `, ${order.customer.name}` : ""}
          </p>
          <h1 className={cn(h1Class, "mt-0.5")}>Order #{order.orderNumber}</h1>
          <div className="flex items-center gap-3 mt-3">
            <span className="text-sm text-muted-foreground">
              {new Date(order.orderDate).toLocaleDateString("en-IN", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            </span>
            <OrderStatusBadge
              status={order.status}
              label={STATUS_LABEL[order.status] ?? order.status}
            />
          </div>
        </div>

        {/* Print preview: swipe through the polaroids before they're printed */}
        {order.printPreviews?.map((preview) => (
          <div key={preview.previewLink} className="px-6 pb-5">
            <a
              href={`/public/print-previews/${preview.previewLink}`}
              className="flex items-center gap-3 rounded-xl bg-primary px-4 py-3 text-sm text-primary-foreground"
            >
              <span className="text-lg" aria-hidden>
                ✨
              </span>
              <span className="flex-1">
                <span className="block font-medium">
                  {preview.submittedAt
                    ? "Your polaroid picks are with us"
                    : "Your polaroids are ready to preview"}
                </span>
                <span className="block text-xs opacity-80">
                  {preview.submittedAt
                    ? "Open them again or change your answers →"
                    : "Swipe through them before we print →"}
                </span>
              </span>
            </a>
          </div>
        ))}

        {/* Items */}
        {order.items && order.items.length > 0 && (
          <div className="px-6 py-5 border-t border-border">
            <p className={cn(metaLabelClass, "mb-3")}>Order Items</p>

            <div>
              {order.items.map((item) => (
                <div
                  key={item.id}
                  className="flex justify-between items-start py-3 border-b border-border last:border-0"
                >
                  <div>
                    <p className="text-sm font-medium text-foreground">{item.productName}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {item.size} · {item.quantity} × ₹{Number(item.unitPrice).toFixed(2)}
                    </p>
                  </div>
                  <p className="text-sm font-medium text-foreground ml-4 shrink-0">
                    ₹{Number(item.lineTotal).toFixed(2)}
                  </p>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="mt-3 pt-3 border-t border-border space-y-1.5">
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>Subtotal</span>
                <span>₹{subtotal.toFixed(2)}</span>
              </div>
              {discountAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    Discount
                    {discountType === "percentage" && ` (${discountValue}%)`}
                  </span>
                  <span className="text-destructive">−₹{discountAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>Shipping</span>
                <span>₹{Number(order.shippingCost ?? 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between pt-1.5 border-t border-border">
                <span className="text-sm font-semibold text-foreground">Total</span>
                <span className="text-base font-semibold text-foreground">₹{total.toFixed(2)}</span>
              </div>
            </div>

            {order.expectedDeliveryDate && (
              <p className="text-xs text-muted-foreground mt-4">
                Expected delivery:{" "}
                {new Date(order.expectedDeliveryDate).toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </p>
            )}
          </div>
        )}

        {/* Shipping & Tracking — shown once shipped, with live timeline when we have data */}
        {(order.status === "shipped" || order.status === "delivered") && order.trackingId && (
          <div className="px-6 py-5 border-t border-border bg-info/5">
            <div className="flex items-start gap-2.5">
              <Truck className="size-4 text-info mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">
                  {order.status === "delivered"
                    ? "Your order has been delivered"
                    : "Your order is on the way!"}
                </p>
                <div className="mt-2 space-y-1">
                  <p className="text-xs text-muted-foreground">
                    <span className="text-muted-foreground">Via </span>
                    <span className="font-medium text-foreground capitalize">
                      {order.shippingProvider || "speedpost"}
                    </span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    <span className="text-muted-foreground">Tracking </span>
                    <span className="font-mono text-foreground">{order.trackingId}</span>
                  </p>
                </div>

                {order.tracking && order.tracking.events.length > 0 ? (
                  <div className="mt-4">
                    <p className={cn(metaLabelClass, "mb-3")}>Shipment progress</p>
                    <TrackingTimeline events={order.tracking.events} />
                  </div>
                ) : (
                  order.shippingProvider === "speedpost" && (
                    <a
                      href={`https://speedposttrack.io/tracking-result?d=${encodeURIComponent(btoa(JSON.stringify({ t: order.trackingId, c: order.shippingProvider })))}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 mt-3 text-xs font-medium text-info hover:underline"
                    >
                      <ExternalLink className="size-3.5" />
                      Track your shipment
                    </a>
                  )
                )}
              </div>
            </div>
          </div>
        )}

        {/* Payment — unpaid */}
        {!isPaid && (
          <div className="px-6 py-6 border-t border-border">
            <p className={cn(metaLabelClass, "mb-5")}>Complete Payment</p>

            <div className="flex flex-col items-center gap-4">
              <div className="text-center">
                <p className={cn(displayClass, "tabular-nums")}>₹{total.toFixed(2)}</p>
                <p className="text-xs text-muted-foreground mt-1">Order #{order.orderNumber}</p>
              </div>

              <div className="border border-border rounded-xl p-3 bg-white">
                <img src={qrSrc} alt="UPI QR Code" className="block w-[180px] h-[180px]" />
              </div>

              <div className="text-center space-y-0.5">
                <p className="font-mono text-[0.9em] font-medium text-foreground">{UPI_ID}</p>
                <p className="text-xs text-muted-foreground">Scan with any UPI app to pay</p>
              </div>

              <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-2">
                <img src="/upi/bhim.svg" alt="BHIM" className="h-6 w-auto" />
                <img src="/upi/upi.svg" alt="UPI" className="h-6 w-auto" />
                <img src="/upi/gpay.svg" alt="Google Pay" className="h-5 w-auto" />
                <img src="/upi/phonepe.svg" alt="PhonePe" className="h-5 w-auto" />
                <img src="/upi/paytm.svg" alt="Paytm" className="h-5 w-auto" />
                <img src="/upi/amazon.svg" alt="Amazon Pay" className="h-5 w-auto" />
              </div>

              <Button variant="outline" className="w-full" asChild>
                <a href={upiUrl}>
                  <Smartphone className="size-4" />
                  Open in UPI App
                </a>
              </Button>

              <p className="text-xs text-muted-foreground text-center leading-relaxed max-w-xs">
                Once payment is done, we'll reach out to confirm your order. This page may take a
                little while to reflect the updated status.
              </p>
            </div>
          </div>
        )}

        {/* Payment — paid */}
        {isPaid && (
          <div className="px-6 py-5 border-t border-border">
            <div className="flex items-center gap-2.5 text-success">
              <CheckCircle2 className="size-5 shrink-0" />
              <p className="text-sm font-medium">Payment received — thank you!</p>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border">
          <p className="text-xs text-muted-foreground text-center">
            © {new Date().getFullYear()} Riqah Design
          </p>
        </div>
      </div>
    </div>
  );
}
